import { Box, HStack, VStack } from "@chakra-ui/react";
import {
  AddCircle as AddCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { FieldArray, useField } from "formik";
import { FC, useCallback } from "react";

import { SymbolOutlineButton } from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import {
  FormikSelectField,
  type FormikSelectFieldProps,
} from "~/src/components/common/forms/FormikSelectField";
import { IconButton } from "~/src/components/common/IconButton";
import { Switch } from "~/src/components/common/Switch";
import { useOpenState } from "~/src/lib/hooks";

export type SelectResourceListSelectProps = Omit<
  FormikSelectFieldProps,
  "name" | "options"
>;

export type Props = {
  options: ReadonlyArray<{ label: string; value: string }>;
  name: string;
  label: string;
  selectProps?: SelectResourceListSelectProps;
};

export const SelectResourceListField: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ options, name, label, selectProps }) => {
  const [resourceIds, , { setValue }] = useField<string[]>(name);

  const { isOpen, onOpen, onClose } = useOpenState(
    resourceIds.value ? resourceIds.value.length > 0 : false
  );

  const handleChange = useCallback(() => {
    if (isOpen) {
      onClose();
      setValue([]);
    } else {
      onOpen();
    }
  }, [isOpen, onClose, onOpen, setValue]);

  const hideLabel = label === "";

  return (
    <FieldArray
      name={name}
      // eslint-disable-next-line react/jsx-no-bind
      render={(arrayHelpers) => (
        <FormControl label={label} hideLabel={hideLabel}>
          <Box width="100%" display="block">
            <Switch isChecked={isOpen} onChange={handleChange} />
          </Box>
          <VStack width="100%">
            {isOpen && (
              <>
                {resourceIds.value &&
                  resourceIds.value.length > 0 &&
                  resourceIds.value.map((id, index) => (
                    <HStack key={index} width="100%">
                      <Box flex={1}>
                        <FormikSelectField
                          name={`${name}.${index}`}
                          options={options}
                          {...selectProps}
                        />
                      </Box>
                      <Box>
                        <IconButton
                          size="24px"
                          // eslint-disable-next-line react/jsx-no-bind
                          onClick={() => arrayHelpers.remove(index)}
                          icon={<DeleteOutlineIcon />}
                        />
                      </Box>
                    </HStack>
                  ))}
                <Box width="100%">
                  <SymbolOutlineButton
                    icon={<AddCircleIcon />}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => arrayHelpers.push("")}
                  >
                    条件を追加
                  </SymbolOutlineButton>
                </Box>
              </>
            )}
          </VStack>
        </FormControl>
      )}
    />
  );
};
